import {
  search,
  autocomplete,
} from '@wix/ambassador-devcenter-ams-v1-app-market-search/http';
import type {
  AutocompleteResponse,
  SearchResponse,
} from '@wix/ambassador-devcenter-ams-v1-app-market-search/types';
import { queryMarketListing } from '@wix/ambassador-devcenter-app-market-listing-v1-market-listing/http';
import { Status } from '@wix/ambassador-devcenter-app-market-listing-v1-market-listing/types';
import { queryApp } from '@wix/ambassador-devcenter-apps-v1-app/http';
import type { Essentials } from '../../../models';
import type { AppSuggestion } from '@wix/app-market-components';

export interface AppsSuggestionsFromVespaRequest {
  httpClient: Essentials['httpClient'];
  searchTerm: string;
  limit?: number;
  offset?: number;
  languageCode?: string;
}

export interface AppsSuggestionsFromVespaResponse {
  suggestions: AppSuggestion[];
}

export interface AutocompleteSuggestionsFromVespaRequest {
  httpClient: Essentials['httpClient'];
  searchTerm: string;
  limit?: number;
  offset?: number;
}

export interface AutocompleteSuggestionsFromVespaResponse {
  suggestions: string[];
}

export const getAppsSuggestionsFromVespa = async ({
  httpClient,
  searchTerm,
  limit = 3,
  offset = 0,
  languageCode = 'en',
}: AppsSuggestionsFromVespaRequest): Promise<AppsSuggestionsFromVespaResponse> => {
  let suggestions: AppSuggestion[] = [];
  const suggestionsIds = await httpClient
    .request<SearchResponse>(
      search({
        searchTerm,
        paging: { limit, offset },
      }),
    )
    .then(({ data }) =>
      data.webSolutions?.map((webSolution) => webSolution.id ?? ''),
    )
    .catch(() => []);

  if (suggestionsIds?.length) {
    const marketListing = await httpClient
      .request(
        queryMarketListing({
          query: {
            filter: {
              appId: suggestionsIds,
              languageCode,
              status: Status.PUBLISHED,
            },
          },
        }),
      )
      .then(({ data }) => data.marketListing)
      .catch(() => []);

    const apps = await httpClient
      .request(
        queryApp({
          query: {
            filter: { id: suggestionsIds },
          },
        }),
      )
      .then(({ data }) => data.apps)
      .catch(() => []);

    suggestions =
      marketListing?.map((marketListingItem) => {
        return {
          id: marketListingItem.appId ?? '',
          name: marketListingItem?.basicInfo?.name ?? '',
          teaser: marketListingItem?.basicInfo?.teaser ?? '',
          icon: marketListingItem?.assetsMap?.appIcon?.assets?.[0]?.url ?? '',
          slug:
            apps?.find((app) => app.id === marketListingItem.appId)?.slug ?? '',
        };
      }) ?? [];
  }

  return {
    suggestions,
  };
};

export const getAutocompleteSuggestionsFromVespa = async ({
  httpClient,
  searchTerm,
  limit = 3,
  offset = 0,
}: AutocompleteSuggestionsFromVespaRequest): Promise<AutocompleteSuggestionsFromVespaResponse> => {
  const suggestions = await httpClient
    .request<AutocompleteResponse>(
      autocomplete({
        searchTerm,
        paging: { limit, offset },
      }),
    )
    .then(({ data }) => data?.suggestions ?? [])
    .catch(() => []);
  return { suggestions };
};
